import React, { useMemo } from "react";

const OPTIONAL_FIELDS = [
  "google_maps_link",
  "instagram",
  "facebook",
  "tiktok",
  "X",
  "telephone_number",
  "address",
  "description",
  "admin",
  "manager",
  "employee",
];

const isNA = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v !== "string") return false;
  const s = v.trim().toLowerCase();
  return s === "" || s === "n/a" || s === "na" || s === "none" || s === "-";
};

export function cleanPayload(obj = {}) {
  const out = { ...obj };
  delete out.PK;
  delete out.SK;

  Object.keys(out).forEach((k) => {
    if (typeof out[k] === "string") out[k] = out[k].trim();
  });

  OPTIONAL_FIELDS.forEach((k) => {
    if (k in out && isNA(out[k])) delete out[k];
  });

  Object.keys(out).forEach((k) => {
    const v = out[k];
    if (typeof v === "string" && isNA(v)) delete out[k];
  });

  return out;
}

export default function VendorForm({ mode, vendor, onChange, onSubmit, onCancel }) {
  const title = useMemo(() => (mode === "edit" ? "Update Vendor" : "Add Vendor"), [mode]);

  return (
    <form onSubmit={onSubmit} className="mb-2">
      <h4 className="mb-3">{title}</h4>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="section-title">Basics</div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                value={vendor.name || ""}
                onChange={(e) => onChange({ ...vendor, name: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Address</label>
              <input
                type="text"
                className="form-control"
                value={vendor.address || ""}
                onChange={(e) => onChange({ ...vendor, address: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="2"
                value={vendor.description || ""}
                onChange={(e) => onChange({ ...vendor, description: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="section-title">Team & Access</div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Owner (Email)</label>
              <input
                type="email"
                className="form-control"
                value={vendor.owner || ""}
                onChange={(e) => onChange({ ...vendor, owner: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Admin (comma-separated emails)</label>
              <input
                type="text"
                className="form-control"
                value={vendor.admin || ""}
                onChange={(e) => onChange({ ...vendor, admin: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Manager (comma-separated emails)</label>
              <input
                type="text"
                className="form-control"
                value={vendor.manager || ""}
                onChange={(e) => onChange({ ...vendor, manager: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Employee (comma-separated emails)</label>
              <input
                type="text"
                className="form-control"
                value={vendor.employee || ""}
                onChange={(e) => onChange({ ...vendor, employee: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="section-title">Links & Contacts</div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Google Maps</label>
              <input
                type="text"
                className="form-control"
                value={vendor.google_maps_link || ""}
                onChange={(e) => onChange({ ...vendor, google_maps_link: e.target.value })}
                placeholder="Link or leave blank"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Telephone</label>
              <input
                type="text"
                className="form-control"
                value={vendor.telephone_number || ""}
                onChange={(e) => onChange({ ...vendor, telephone_number: e.target.value })}
                placeholder="+34 600 000 000 (optional)"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Instagram</label>
              <input
                type="text"
                className="form-control"
                value={vendor.instagram || ""}
                onChange={(e) => onChange({ ...vendor, instagram: e.target.value })}
                placeholder="@handle or link"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Facebook</label>
              <input
                type="text"
                className="form-control"
                value={vendor.facebook || ""}
                onChange={(e) => onChange({ ...vendor, facebook: e.target.value })}
                placeholder="Link or leave blank"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">TikTok</label>
              <input
                type="text"
                className="form-control"
                value={vendor.tiktok || ""}
                onChange={(e) => onChange({ ...vendor, tiktok: e.target.value })}
                placeholder="@handle or link"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">X (Twitter)</label>
              <input
                type="text"
                className="form-control"
                value={vendor.X || ""}
                onChange={(e) => onChange({ ...vendor, X: e.target.value })}
                placeholder="@handle or link"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <div className="section-title mb-1">Status</div>
            <div className="text-muted small">Toggle to disable vendor access.</div>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="activeSwitch"
              checked={vendor.active !== false}
              onChange={(e) => onChange({ ...vendor, active: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="activeSwitch">
              Active
            </label>
          </div>
        </div>
      </div>

      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary">
          {mode === "edit" ? "Update Vendor" : "Add Vendor"}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
